import React, { useState, useEffect, useRef, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import CustomerDashboard from "./pages/CustomerDashboard";
import LoanApplication from "./pages/LoanApplication";
import LoanOfficerDashboard from "./pages/LoanOfficerDashboard";
import ApplicationReview from "./pages/ApplicationReview";
import KnowledgePortal from "./pages/KnowledgePortal";
import LoanProducts from "./pages/LoanProducts";
import CreditDecisioning from "./pages/CreditDecisioning";
import AnalyticsDashboard from "./pages/AnalyticsDashboard";
import WholesaleLending from "./pages/WholesaleLending";
import DeviceFinance from "./pages/DeviceFinance";
import WholesaleApplication from "./pages/WholesaleApplication";
import BusinessLoanApplication from "./pages/BusinessLoanApplication";
import DeviceFinanceApplication from "./pages/DeviceFinanceApplication";
import IndividualLoans from "./pages/IndividualLoans";
import IndividualLoanApplication from "./pages/IndividualLoanApplication";
import AdminDashboard from "./pages/AdminDashboard";
// Back Office Pages
import BackOfficeApplications from "./pages/BackOfficeApplications";
import BackOfficeReview from "./pages/BackOfficeReview";
import BackOfficeUsers from "./pages/BackOfficeUsers";
import BackOfficeAnalytics from "./pages/BackOfficeAnalytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));

  const checkAuth = async () => {
    const storedToken = localStorage.getItem("token");
    if (!storedToken) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${storedToken}` },
        withCredentials: true
      });
      setUser(response.data);
      setToken(storedToken);
    } catch (error) {
      localStorage.removeItem("token");
      setUser(null);
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("token", response.data.token);
    setToken(response.data.token);
    setUser(response.data);
    return response.data;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    localStorage.setItem("token", response.data.token);
    setToken(response.data.token);
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (e) {
      console.error("Logout error:", e);
    }
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    setUser,
    setToken,
    checkAuth,
    isAuthenticated: !!user,
    isLoanOfficer: user?.role === "loan_officer",
    isBackOffice: ["loan_officer", "manager", "executive", "admin"].includes(user?.role)
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Auth Callback for Google OAuth
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, setToken } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await axios.post(
            `${API}/auth/google/session`,
            { session_id: sessionId },
            { withCredentials: true }
          );
          
          localStorage.setItem("token", response.data.token);
          setToken(response.data.token);
          setUser(response.data);
          
          toast.success("Successfully logged in with Google!");
          
          // Redirect based on role
          if (response.data.role === "loan_officer") {
            navigate("/officer/dashboard", { replace: true });
          } else {
            navigate("/dashboard", { replace: true });
          }
        } catch (error) {
          console.error("Google auth error:", error);
          toast.error("Google login failed. Please try again.");
          navigate("/login", { replace: true });
        }
      } else {
        navigate("/login", { replace: true });
      }
    };

    processAuth();
  }, [location, navigate, setUser, setToken]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0A2540] mx-auto"></div>
        <p className="mt-4 text-gray-600">Completing authentication...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0A2540]"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect based on role
    const backOfficeRoles = ["loan_officer", "manager", "executive", "admin"];
    if (backOfficeRoles.includes(user.role)) {
      return <Navigate to="/backoffice/applications" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();

  // Check for session_id in hash (Google OAuth callback)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/products" element={<LoanProducts />} />
      <Route path="/products/apply" element={<BusinessLoanApplication />} />
      <Route path="/wholesale-lending" element={<WholesaleLending />} />
      <Route path="/wholesale-lending/apply" element={<WholesaleApplication />} />
      <Route path="/individual-loans" element={<IndividualLoans />} />
      <Route path="/individual-loans/apply" element={<IndividualLoanApplication />} />
      <Route path="/device-finance" element={<DeviceFinance />} />
      <Route path="/device-finance/apply" element={<DeviceFinanceApplication />} />
      <Route path="/knowledge" element={<KnowledgePortal />} />
      
      {/* Customer Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute allowedRoles={["customer"]}>
            <CustomerDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/apply"
        element={
          <ProtectedRoute allowedRoles={["customer"]}>
            <LoanApplication />
          </ProtectedRoute>
        }
      />
      
      {/* Back Office Routes - All internal staff */}
      <Route
        path="/backoffice/applications"
        element={
          <ProtectedRoute allowedRoles={["loan_officer", "manager", "executive", "admin"]}>
            <BackOfficeApplications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/backoffice/review/:applicationId"
        element={
          <ProtectedRoute allowedRoles={["loan_officer", "manager", "executive", "admin"]}>
            <BackOfficeReview />
          </ProtectedRoute>
        }
      />
      <Route
        path="/backoffice/analytics"
        element={
          <ProtectedRoute allowedRoles={["loan_officer", "manager", "executive", "admin"]}>
            <BackOfficeAnalytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/backoffice/users"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <BackOfficeUsers />
          </ProtectedRoute>
        }
      />
      
      {/* Legacy Loan Officer Routes - Redirect to Back Office */}
      <Route
        path="/officer/dashboard"
        element={<Navigate to="/backoffice/applications" replace />}
      />
      <Route
        path="/officer/review/:applicationId"
        element={
          <ProtectedRoute allowedRoles={["loan_officer", "manager", "executive", "admin"]}>
            <BackOfficeReview />
          </ProtectedRoute>
        }
      />
      <Route
        path="/officer/credit-decisioning"
        element={<Navigate to="/backoffice/applications" replace />}
      />
      <Route
        path="/officer/analytics"
        element={<Navigate to="/backoffice/analytics" replace />}
      />
      
      {/* Admin Routes */}
      <Route
        path="/admin"
        element={<Navigate to="/backoffice/users" replace />}
      />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-center" richColors duration={5000} />
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
export { API, BACKEND_URL };
